import React, { useState, useRef, useEffect } from "react";
import { Upload, AlertCircle, Link, FileText, Eye, Download, Trash2, X } from "lucide-react";
import "../../styles/CompanyManagement/CompanyDocuments.css";
import { uploadCompanyKnowledgeFiles } from "../../utils/Product";

import axiosInstance from "../../utils/api";
import UnifiedDocumentModal from "../Common/UnifiedDocumentModal";
import { useCompany } from "../../context/CompanyContext";

const CompanyDocuments = () => {
    const { companyId, companyKnowledgeDocs, loadCompanyKnowledgeBase } = useCompany();
    const [files, setFiles] = useState([]);
    const [urls, setUrls] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const fileInputRef = useRef(null);
    const urlInputRef = useRef(null);

    // Load existing company knowledge base files when companyId changes
    useEffect(() => {
        if (companyId) {
            loadCompanyKnowledgeBase();
        }
    }, [companyId, loadCompanyKnowledgeBase]);

    // Update local files when companyKnowledgeDocs change
    useEffect(() => {
        if (companyKnowledgeDocs.length > 0) {
            setUrls(companyKnowledgeDocs.map(doc => doc.url));
            
            // Convert to file objects for display
            const existingFiles = companyKnowledgeDocs.map((doc, index) => ({
                id: `existing-${index}`,
                name: doc.name,
                type: doc.content_type || getFileTypeFromUrl(doc.url),
                status: "completed",
                url: doc.url,
                size: doc.size || 0,
                isExisting: true
            }));
            setFiles(existingFiles);
        }
    }, [companyKnowledgeDocs]);

    // Refresh knowledge base after uploads
    const refreshCompanyKnowledgeBase = async () => {
        if (companyId) {
            await loadCompanyKnowledgeBase();
        }
    };





    const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const validateFile = (file) => {
        if (!allowedTypes.includes(file.type)) {
            setError("Invalid file type. Please upload PDF, JPEG, PNG or DOC files.");
            return false;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError("File size should not exceed 10MB.");
            return false;
        }
        return true;
    };

    const getFileTypeFromUrl = (url) => {
        const filename = url.split("/").pop() || "";
        if (filename.includes(".pdf")) {
            return "application/pdf";
        } else if (filename.includes(".jpg") || filename.includes(".jpeg") || filename.includes(".png")) {
            return "image/jpeg";
        } else if (filename.includes(".doc") || filename.includes(".docx")) {
            return "application/msword";
        } else if (filename.includes(".csv")) {
            return "text/csv";
        }
        return "application/octet-stream";
    };

    const getFileIcon = (fileType) => {
        if (fileType.includes("pdf")) {
            return "/pdf.svg";
        } else if (fileType.includes("image")) {
            return "/img.svg";
        } else if (fileType.includes("word")) {
            return "/doc.svg";
        } else if (fileType.includes("csv")) {
            return "/doc.svg";
        }
        return "/doc.svg";
    };

    const uploadFile = async (file) => {
        const tempId = Date.now();
        try {
            setUploading(true);
            setError("");

            // Add a placeholder entry so that UI immediately reflects uploading state
            setFiles((prev) => [
                ...prev,
                {
                    id: tempId,
                    name: file.name,
                    type: file.type,
                    status: "uploading",
                    progress: 0,
                    size: file.size,
                },
            ]);


            // Simulate upload progress
            const progressInterval = setInterval(() => {
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === tempId
                            ? { ...f, progress: Math.min(f.progress + Math.random() * 20, 90) }
                            : f
                    )
                );
            }, 200);

            // Create FormData for the upload
            const formData = new FormData();
            formData.append("file[]", file);
            
            // Get the current company ID dynamically
            const currentCompanyId = await getCurrentCompanyId();
            if (!currentCompanyId) {
                setError("Could not determine company ID. Please try again.");
                return;
            }
            
            const jsonPayload = {
                request_type: "blog",
                additional_data: {
                    documents: [currentCompanyId],
                    uid: Date.now().toString() + "-" + Math.random().toString(36).substring(2, 6),
                },
            };
            formData.append("request_json", JSON.stringify(jsonPayload));

            // Perform the upload using the new company knowledge upload function
            const response = await uploadCompanyKnowledgeFiles(formData);

            // Clear progress interval and set to 100%
            clearInterval(progressInterval);
            setFiles((prev) =>
                prev.map((f) =>
                    f.id === tempId ? { 
                        ...f, 
                        status: "completed", 
                        progress: 100,
                        url: response.files?.[0] || "",
                        uploadId: response.response?.id || tempId
                    } : f
                )
            );
            
            // Update the URLs state if we have a response
            if (response.files && response.files.length > 0) {
                setUrls((prev) => [...prev, ...response.files]);
                
                // Refresh the company knowledge base to show the new documents
                await refreshCompanyKnowledgeBase();
            }

        } catch (err) {
            console.error('Company knowledge upload error:', {
                error: err,
                message: err.message,
                stack: err.stack,
                file: {
                    name: file.name,
                    size: file.size,
                    type: file.type
                }
            });
            
            setError(`Upload failed: ${err.message || 'Unknown error occurred'}`);
            setFiles((prev) => prev.filter((f) => f.id !== tempId));
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;
        
        setError("");
        
        for (const file of selectedFiles) {
            if (!validateFile(file)) continue;
            try {
                await uploadFile(file);
            } catch (err) {
                console.error('Error in handleFileSelect:', err);
                // Error is already handled in uploadFile
            }
        }
        e.target.value = null;
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        for (const file of droppedFiles) {
            if (validateFile(file)) {
                await uploadFile(file);
            }
        }
    };

    const handleCloudService = (service) => {
    };

    const handleUrlUpload = async () => {
        const url = urlInputRef.current.value;
        if (!url) {
            setError("Please enter a valid URL");
            return;
        }

        try {
            setUploading(true);

            const fileName = url.split("/").pop() || "downloaded-file";
            const uploadedFile = {
                id: Date.now(),
                name: fileName,
                type: "url",
                status: "uploading",
                progress: 0,
            };

            setFiles((prev) => [...prev, uploadedFile]);

            // Create FormData for the URL upload
            const formData = new FormData();
            
            // Get the current company ID dynamically
            const currentCompanyId = await getCurrentCompanyId();
            if (!currentCompanyId) {
                setError("Could not determine company ID. Please try again.");
                return;
            }
            
            const jsonPayload = {
                request_type: "blog",
                additional_data: {
                    documents: [currentCompanyId],
                    uid: Date.now().toString() + "-" + Math.random().toString(36).substring(2, 6),
                },
            };
            formData.append("request_json", JSON.stringify(jsonPayload));

            try {
                // Use the website upload endpoint for URLs
                const response = await axiosInstance.post("/upload/website", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: localStorage.getItem("token"),
                    },
                });

                if (response.status === 200) {
                    setFiles((prev) =>
                        prev.map((f) =>
                            f.id === uploadedFile.id ? { 
                                ...f, 
                                status: "completed",
                                url: url,
                                uploadId: response.data?.id || uploadedFile.id
                            } : f
                        )
                    );
                    
                    // Add URL to the URLs state
                    setUrls((prev) => [...prev, url]);
                    
                    // Refresh the company knowledge base to show the new documents
                    await refreshCompanyKnowledgeBase();
                    

                }
            } catch (uploadErr) {
                console.error("URL upload failed:", uploadErr);
                setError("Failed to process URL");
            }

            urlInputRef.current.value = "";
        } catch (err) {
            setError("Failed to upload file from URL");
        } finally {
            setUploading(false);
        }
    };

    const getCurrentCompanyId = async () => {
        try {
            // First try to get existing companies
            const companiesResponse = await axiosInstance.get('/api/v1/companies');
            const companies = companiesResponse.data;
            
            if (companies && companies.length > 0) {
                return companies[0].id; // Return first company (ensure only one per user)
            }
            
            // If no company exists, create one
            const newCompany = await createDefaultCompany();
            return newCompany ? newCompany.id : null;
        } catch (error) {
            console.error('Failed to get current company ID:', error);
            return null;
        }
    };

    const createDefaultCompany = async () => {
        try {
            // Get current user info to create a default company
            const userResponse = await axiosInstance.get('/current_user');
            const userData = userResponse.data;
            
            const defaultCompanyData = {
                company: {
                    name: `${userData.name || 'My Company'}'s Company`,
                    email: userData.email || 'company@example.com',
                    website: '',
                    location: '',
                    category: 'Technology',
                    problem_statement: '',
                    usp: '',
                    additional_infos: {
                        knowledge_base: []
                    }
                }
            };
            
            const createResponse = await axiosInstance.post('/api/v1/companies', defaultCompanyData);
            
            // Update the company context
            if (window.updateCompanyContext) {
                window.updateCompanyContext(createResponse.data.company.id);
            }
            
            return createResponse.data.company;
        } catch (error) {
            console.error('Failed to create default company:', error);
            return null;
        }
    };



    const persistCompanyKnowledgeBase = async (uploadedFiles) => {
        try {
            // Get current company ID dynamically
            const currentCompanyId = await getCurrentCompanyId();
            if (!currentCompanyId) {
                console.error('Could not determine company ID for persistence');
                return;
            }
            
            // Get current company data
            const response = await axiosInstance.get(`/api/v1/companies/${currentCompanyId}`);
            const companyData = response.data;
            
            const currentAdditionalInfos = companyData.additional_infos || {};
            const currentKnowledgeBase = currentAdditionalInfos.knowledge_base || [];
            
            // Add new files to knowledge base
            const updatedKnowledgeBase = [...currentKnowledgeBase, ...uploadedFiles];
            
            // Update company with new knowledge base
            await axiosInstance.patch(`/api/v1/companies/${currentCompanyId}`, {
                company: {
                    additional_infos: {
                        ...currentAdditionalInfos,
                        knowledge_base: updatedKnowledgeBase
                    }
                }
            });
            
        } catch (error) {
            console.error('Failed to persist files to company knowledge base:', error);
        }
    };

    const handleDelete = async (fileId) => {
        try {
            // Find the file to get its URL
            const fileToDelete = files.find(f => f.id === fileId);
            if (!fileToDelete) return;

            // Remove from local state immediately for better UX
            setFiles((prev) => prev.filter((f) => f.id !== fileId));
            setUrls((prev) => prev.filter(url => url !== fileToDelete.url));

            // TODO: Add API call to delete from backend if needed
        } catch (error) {
            console.error('Error deleting file:', error);
            setError('Failed to delete file. Please try again.');
        }
    };

    const handleView = (file) => {
        if (file.url) {
            window.open(file.url, '_blank');
        }
    };

    const handleDownload = (file) => {
        if (file.url) {
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="container">
            {loading && (
                <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading company knowledge base...</p>
                </div>
            )}
            
            {error && (
                <div
                    className="alert alert-danger d-flex align-items-center"
                    role="alert"
                >
                    <AlertCircle size={18} className="me-2" />
                    {error}
                    <button
                        type="button"
                        className="btn-close ms-auto"
                        onClick={() => setError("")}
                    />
                </div>
            )}
            <div className="knowledgeBaseContainer">
                <h3>Knowledge Base - Company documents</h3>
                <div className="separator" />
                <h5 className="mt-4">Upload essential business documents</h5>
                <p>
                    Share documents like <a href="#">brand guidelines</a> and <a href="#">pitch decks</a>,
                    etc. to help us tailor growth  <br /> strategies for your business.
                </p>
            </div>
            <div className="row g-4 mt-4">

                <div className="col-md-4">
                    <p className="text-center mb-2"><strong>Connect Your Cloud Files</strong></p>
                    <div>
                        <div className="cloud-services">
                            <button
                                className="cloud-btn google btn"
                                onClick={() => handleCloudService("Google Drive")}
                            >
                                <img src="/drive.svg" alt="Google Drive" />
                            </button>
                            <button
                                className="cloud-btn amazon btn"
                                onClick={() => handleCloudService("Amazon")}
                            >
                                <img src="/s3.svg" alt="Amazon" />
                            </button>
                            <button
                                className="cloud-btn onedrive btn"
                                onClick={() => handleCloudService("OneDrive")}
                            >
                                <img src="/onedrive.svg" alt="OneDrive" />
                            </button>
                            <button
                                className="cloud-btn dropbox btn"
                                onClick={() => handleCloudService("Dropbox")}
                            >
                                <img src="/dropbox.svg" alt="Dropbox" />
                            </button>
                        </div>
                        <div className="url-input mt-3">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Link size={18} />
                                </span>
                                <input
                                    ref={urlInputRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Type or paste the URL"
                                />
                                <button
                                    className="btn linkuploadbtn"
                                    onClick={handleUrlUpload}
                                    disabled={uploading}
                                >
                                    <Upload size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-1">
                    <p className="text-center mt-5"><strong>or</strong></p>
                </div>
                <div className="col-md-4">
                    <p className="text-start mb-3">
                        <strong>Drag and drop files,or Browse</strong>
                    </p>
                    <div
                        className={`upload-box ${dragActive ? "drag-active" : ""}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="d-none"
                            onChange={handleFileSelect}
                            accept={allowedTypes.join(",")}
                        />
                        <Upload size={24} />
                        <p>
                            Drag and drop files, or{" "}
                            <span className="browse-link">Browse</span>
                        </p>
                        <small className="text-muted">
                            Supported formats: PDF, JPEG, PNG, DOC (Max 10MB)
                        </small>
                    </div>
                </div>


            </div>

            {files.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="uploaded-files-header">
                            <h6><strong>Uploaded documents</strong></h6>
                            <span className="manage-files">Manage your uploaded files</span>
                        </div>

                        {/* Uploaded files container */}
                        <div className="uploaded-files">
                            {files.map((file) => (
                                <div key={file.id}>
                                    {/* File Card (Only Image) */}
                                    <div className={`file-card ${file.status === "uploading" ? "progress-card" : ""}`}>
                                        {file.status === "uploading" ? (
                                            <>
                                                <div className="progress-circle">
                                                    <i className="fas fa-sync-alt fa-spin"></i>
                                                </div>
                                                <div className="file-info">
                                                    <div className="file-name text-white">
                                                        Uploading...
                                                    </div>
                                                    <div className="progress-value">{file.progress}%</div>
                                                    <div className="progress">
                                                        <div
                                                            className="progress-bar"
                                                            style={{ width: `${file.progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {file.status === "updating" && (
                                                    <span className="updating-label">Updating</span>
                                                )}
                                                <div className="file-icon">
                                                    <img src={getFileIcon(file.type)} alt={file.type} />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* File Info Below the File Card */}
                                    <div className="file-info">
                                        <div className="file-name">{file.name}</div>
                                        <div className="file-size">{formatFileSize(file.size)}</div>
                                        <div className="file-actions">
                                            <button
                                                className="action-btn view-btn"
                                                onClick={() => handleView(file)}
                                                title="View file"
                                                disabled={!file.url}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                className="action-btn download-btn"
                                                onClick={() => handleDownload(file)}
                                                title="Download file"
                                                disabled={!file.url}
                                            >
                                                <Download size={16} />
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => handleDelete(file.id)}
                                                title="Delete file"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <hr></hr>

            <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end">
                    <button className="btn me-2">Back</button>
                    {/* <button 
                        className="btn btn-outline-primary me-2"
                        onClick={() => setShowDocumentModal(true)}
                        disabled={files.length === 0}
                    >
                        <FileText size={16} className="me-2" />
                        View in Document Modal
                    </button> */}
                    <button
                        className="btn btn-dark"
                        disabled={uploading || files.length === 0}
                        style={{
                            cursor:
                                uploading || files.length === 0 ? "not-allowed" : "pointer",
                        }}
                    >
                        Continue
                    </button>
                </div>
            </div>
            
            {/* Document Modal for viewing company knowledge base documents */}
                    <UnifiedDocumentModal
          show={showDocumentModal}
          onHide={() => setShowDocumentModal(false)}
        />
        </div>
    );
};

export default CompanyDocuments;





